import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const InstagramFeed = () => {
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => Array.isArray(data) && setPhotos(data))
      .catch((e) => {
        if (process.env.NODE_ENV === "development") console.error("Feed load failed:", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="feed" data-testid="instagram-feed-section" className="py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm tracking-[0.25em] uppercase text-brand font-bold">Our Feed</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-ink">
            Fresh from our kitchen
          </h2>
          <p className="mt-4 text-inkmuted">
            The latest we're cooking up — follow{" "}
            <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" className="text-brand font-semibold hover:underline">
              @{BUSINESS.instagram}
            </a>{" "}
            for more.
          </p>
        </div>

        {photos.length > 0 && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="feed-grid">
            {photos.map((p, i) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.06 }}
                data-testid={`feed-photo-${i + 1}`}
                onClick={() => setActive(p)}
                className="group relative aspect-square overflow-hidden rounded-2xl border border-line bg-white cursor-pointer"
              >
                <img
                  src={`${BACKEND}${p.url}`}
                  alt={p.caption || "KitchenBox dish"}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {p.caption && (
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent text-cream text-xs sm:text-sm font-semibold p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    {p.caption}
                  </figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <a
            href={BUSINESS.instagramUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-btn"
            className="inline-flex items-center gap-2 text-white font-bold rounded-full px-8 py-4 shadow-md bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 transition-opacity"
          >
            <Instagram size={20} /> Follow @{BUSINESS.instagram}
          </a>
        </div>
      </div>

      <Dialog open={Boolean(active)} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="p-0 bg-transparent border-0 shadow-none max-w-3xl w-fit" data-testid="feed-lightbox">
          {active && (
            <figure className="flex flex-col items-center">
              <img
                src={`${BACKEND}${active.url}`}
                alt={active.caption || "KitchenBox dish"}
                className="max-h-[80vh] w-auto rounded-2xl object-contain"
              />
              {active.caption && (
                <figcaption className="mt-3 text-cream text-sm font-semibold bg-ink/70 px-4 py-2 rounded-full">
                  {active.caption}
                </figcaption>
              )}
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
